import {
  selectCurrentHotelId,
  selectCurrentHotelName,
  selectIsInternalAdmin
} from 'accessors'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import Dialog from 'components/ReusableComponents/Dialog'
import FileIcon from 'components/ReusableComponents/FileIcon'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import CampaignSetup, {
  dripMediumOptions,
  dripMediumOptionsWithEmail,
  dripMediumOptionsWithText
} from 'features/CustomCampaigns/EditCampaign/CampaignSetup'
import CampaignStartConfirmation from 'features/CustomCampaigns/EditCampaign/CampaignStartConfirmation'
import MainPromo from 'features/CustomCampaigns/EditCampaign/MainPromo'
import MainTemplate from 'features/CustomCampaigns/EditCampaign/MainTemplate'
import {
  customCampaignStatusTypes as statusTypes,
  getCampaignStatusColor
} from 'features/CustomCampaigns/helpers'
import {
  EmailEditorProvider,
  useEmailEditor
} from 'features/EmailEditor/EmailEditorContext'
import EmailEditorPanel from 'features/EmailEditor/EmailEditorPanel'
import EmailPreview from 'features/EmailEditor/EmailPreview'
import TestEmail from 'features/EmailEditor/TestEmail'
import PromoUsageWarningDialog from 'features/ManageCampaigns/EditPromoDialog/PromoUsageWarningDialog'
import { TextPreview, TextPreviewBubble } from 'features/ManageCampaigns/TextPreview'
import {
  getExtensionFromUrl,
  getImgUrlFromCDN,
  isCampaignImageVisible
} from 'features/ManageCampaigns/utils/campaignHelpers'
import { mediaTags } from 'features/ManageCampaigns/utils/constants'
import useEsc from 'hooks/useEsc'
import usePlausible from 'hooks/usePlausible'
import usePromoValidation from 'hooks/usePromoValidaiton'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import audienceApi from 'services/api/audience'
import campaignApi from 'services/api/campaign'

import { ReactComponent as CloseIcon } from './close.svg'
import styles from './EditCampaign.module.scss'

const steps = {
  campaignSetup: 'campaignSetup',
  mainTemplate: 'mainTemplate',
  email: 'email',
  mainPromo: 'mainPromo'
}

function getSteps(medium) {
  const stepsArray = [steps.campaignSetup]

  if (dripMediumOptionsWithText.includes(medium)) {
    stepsArray.push(steps.mainTemplate)
  }

  if (dripMediumOptionsWithEmail.includes(medium)) {
    stepsArray.push(steps.email)
  }

  stepsArray.push(steps.mainPromo)
  return stepsArray
}

const urlRegex = /^(http|https)?:\/\/\S+/i

const Dot = ({ fill = 'gray' }) => (
  <svg height="8" width="7">
    <circle cx="3.5" cy="3.5" r="3.5" fill={fill} />
  </svg>
)

const mediaTag = mediaTags.campaign

const requiredTags = ['@[hotelName](__hotelName__)', '@[guestName](__guestName__)']

const requiredTagIds = ['__hotelName__', '__guestName__']

const EditCampaign = () => {
  const { id } = useParams()

  const [campaignData, setCampaignData] = useState()

  const [isLoadingCampaign, setIsLoadingCampaign] = useState(false)
  const [isLoadingAudience, setIsLoadingAudience] = useState(false)
  const [deliveryStartDate, setDeliveryStartDate] = useState()
  const [deliveryFinishDate, setDeliveryFinishDate] = useState()

  const [promotionCode, setPromotionCode] = useState()
  const [maxDiscountPercentage, setMaxDiscountPercentage] = useState()
  const [minNightsToStay, setMinNightsToStay] = useState()
  const [promoTagline, setPromoTagline] = useState('Sample promo taglne')

  const [hasPromoValidityDate, setHasPromoValidityDate] = useState(false)
  const [promoValidityStartDate, setPromoValidityStartDate] = useState()
  const [promoValidityEndDate, setPromoValidityEndDate] = useState()
  const [showPromoUsageWarning, setShowPromoUsageWarning] = useState(null)

  const [messageMedia, setMessageMedia] = useState()

  const [customDeliveryStatus, setCustomDeliveryStatus] = useState()

  const [customUrl, setCustomUrl] = useState()

  const [audiences, setAudiences] = useState([])

  const [canModifySetup, setCanModifySetup] = useState(false)

  const history = useHistory()

  const { postEvent, plausibleEvents } = usePlausible()

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const [medium, setMedium] = useState()

  useEffect(() => {
    setIsLoadingCampaign(true)

    campaignApi
      .getCustomCampaignList()
      .then(res => {
        const campaign = res.items.find(c => c.contentId === Number(id))
        setCampaignData(campaign)
      })
      .catch(err => {
        console.warn(err)
      })
      .finally(() => {
        setIsLoadingCampaign(false)
      })
  }, [id, isInternalAdmin])

  useEffect(() => {
    setIsLoadingAudience(true)

    audienceApi
      .getList()
      .then(res => {
        setAudiences(res)
      })
      .catch(err => {
        console.warn(err)
      })
      .finally(() => {
        setIsLoadingAudience(false)
      })
  }, [])

  useEffect(() => {
    if (campaignData) {
      const c = campaignData

      setAudienceId(c.audienceId)
      setPromotionCode(c.promotionCode)
      setPromoTagline(c.promotionTagline)
      setMinNightsToStay(c.minNightsToStay)
      setMaxDiscountPercentage(c.maxDiscountPercentage)
      setMedium(c.medium)

      setDeliveryStartDate(c.deliveryStartDate || moment().toDate())
      setDeliveryFinishDate(c.deliveryFinishDate)

      setMessageTemplate(c.messageTemplate)
      setMessageMedia(c.messageMediaUrls?.[0])

      setUsedAllTags(requiredTags.every(tag => c.messageTemplate.includes(tag)))

      setCustomDeliveryStatus(c.customDeliveryStatus)

      setCustomUrl(c.customUrl)

      setHasPromoValidityDate(c.hasPromoValidityDate)
      setPromoValidityStartDate(
        c.promoValidityStartDate ? new Date(c.promoValidityStartDate) : new Date()
      )
      setPromoValidityEndDate(
        c.promoValidityEndDate
          ? new Date(c.promoValidityEndDate)
          : moment().add(15, 'day').toDate()
      )

      if ([statusTypes.Draft, statusTypes.Approved].includes(c.customDeliveryStatus)) {
        setCanModifySetup(true)
      }

      if (c.customDeliveryStatus === statusTypes.Approved) {
        setActiveStep(steps.mainTemplate)
      }
    }
  }, [campaignData])

  const { emailState, emailHtml, editingSection, refreshEmailData } = useEmailEditor()

  function updateCampaignEmail() {
    const { socialMediaLinks, ...emailParams } = emailState
    return campaignApi.updateCampaignEmail({
      campaignId: id,
      emailParams,
      globalParams: {
        socialMediaLinks
      },
      html: emailHtml
    })
  }

  function updateCampaign(customDeliveryStatus) {
    postEvent(plausibleEvents.UpdateTextBlast, { id })

    function conditionalEmailUpdate() {
      if (dripMediumOptionsWithEmail.includes(medium)) {
        const { subject, preheaderText, selectedTemplate } = emailState

        if (!subject) {
          addToast('Email subject cannot be empty', {
            appearance: 'error',
            autoDismiss: true
          })
          return Promise.reject('Email subject cannot be empty')
        }

        if (!preheaderText) {
          addToast('Email preheader cannot be empty', {
            appearance: 'error',
            autoDismiss: true
          })
          return Promise.reject('Email preheader cannot be empty')
        }

        return campaignApi.updateCampaignEmail({
          campaignId: id,
          emailParams: {
            subject,
            preheaderText,
            selectedTemplate
          },
          html: emailHtml
        })
      } else {
        return Promise.resolve()
      }
    }

    conditionalEmailUpdate()
      .then(() =>
        campaignApi.updateCustomCampaign({
          ...campaignData,
          audienceId,
          deliveryStartDate: moment(deliveryStartDate).format('YYYY-MM-DD'),
          deliveryFinishDate: deliveryFinishDate
            ? moment(deliveryFinishDate).format('YYYY-MM-DD')
            : null,
          messageTemplate,
          messageMediaUrls: messageMedia ? [messageMedia] : [],
          customDeliveryStatus: customDeliveryStatus || campaignData.customDeliveryStatus,
          customUrl,
          promotionCode,
          promotionTagline: promoTagline,
          maxDiscountPercentage,
          minNightsToStay,
          hasPromoValidityDate,
          promoValidityStartDate: moment(promoValidityStartDate).format('YYYY-MM-DD'),
          promoValidityEndDate: moment(promoValidityEndDate).format('YYYY-MM-DD'),
          medium,
          isActive:
            (customDeliveryStatus || campaignData.customDeliveryStatus) ===
            statusTypes.Draft
              ? false
              : true
        })
      )
      .then(() => {
        addToast('Campaign updated successfully', {
          appearance: 'success',
          autoDismiss: true
        })
      })
      .catch(err => {
        console.warn(err)
      })
  }

  const wrapperRef = useRef()

  useEsc(() => {
    if (wrapperRef.current.offsetParent !== null) {
      history.push('/custom-campaigns')
    }
  })

  const { addToast } = useToasts()

  const userName = useSelector(s => s.login.currentUser.name)
  const hotelName = useSelector(selectCurrentHotelName)
  const hotelId = useSelector(selectCurrentHotelId)
  const isHotelSetupCompleted = useSelector(s => s.hotelDetails.isPreOnboardingCompleted)

  const [isNestedDialogActive, setIsNestedDialogActive] = useState(true)

  const [messageTemplate, setMessageTemplate] = useState('')

  const [usedAllTags, setUsedAllTags] = useState(true)

  const [audienceId, setAudienceId] = useState()

  const [isAudienceCalculating, setIsAudienceCalculating] = useState(true)

  useEffect(() => {
    if (audienceId) {
      setIsAudienceCalculating(true)

      audienceApi.get(audienceId).then(res => {
        setIsAudienceCalculating(false)
        setAudiences(prev => prev.map(a => (a.id === res.id ? { ...a, ...res } : a)))
      })
    }
  }, [audienceId])

  function handleTextChange(text) {
    setMessageTemplate(text.replace(/inviteLink__\)(\S)/, 'inviteLink__) $1'))
  }

  const [isConfirmationVisible, setIsConfirmationVisible] = useState()

  const stepNames = {
    campaignSetup: 'Preferences',
    mainTemplate: 'Text content',
    email: 'Email',
    mainPromo: 'Promotion',
    optInPromo: 'Opt-in promotion'
  }

  const [activeStep, setActiveStep] = useState(steps.campaignSetup)

  useEffect(() => {
    if (
      editingSection &&
      editingSection !== 'inbox-preview' &&
      steps.email === activeStep
    ) {
      setIsNestedDialogActive(true)
    } else {
      setIsNestedDialogActive(false)
    }
  }, [editingSection, activeStep])

  const [nextStep, setNextStep] = useState(steps.mainTemplate)
  const [isNextDisabled, setIsNextDisabled] = useState(false)

  const [isValidated, setIsValidated] = useState()

  useEffect(() => {
    const isSetupGood = deliveryStartDate || !canModifySetup
    const isMainTemplateGood = usedAllTags

    setIsValidated(isSetupGood && isMainTemplateGood)
  }, [deliveryFinishDate, deliveryStartDate, usedAllTags, activeStep, canModifySetup])

  useEffect(() => {
    let isGood

    if (activeStep === steps.campaignSetup) {
      isGood = deliveryStartDate
    }

    if (activeStep === steps.mainTemplate) {
      isGood = usedAllTags && (customUrl ? !!customUrl.match(urlRegex) : true)
    }

    if (activeStep === steps.mainPromo) {
      isGood = true
    }

    if (activeStep === steps.email) {
      isGood = true
    }

    setIsNextDisabled(!isGood)
  }, [
    deliveryFinishDate,
    deliveryStartDate,
    usedAllTags,
    activeStep,
    canModifySetup,
    customUrl
  ])

  useEffect(() => {
    function getNextStep() {
      switch (activeStep) {
        case steps.campaignSetup:
          if (dripMediumOptionsWithText.includes(medium)) {
            return steps.mainTemplate
          }

          return steps.email

        case steps.mainTemplate:
          if (dripMediumOptionsWithEmail.includes(medium)) {
            return steps.email
          }

          return steps.mainPromo

        case steps.email:
          return steps.mainPromo

        case steps.mainPromo:
          return null

        default:
          return null
      }
    }

    setNextStep(getNextStep())
  }, [activeStep, medium])

  const [isMissingMediaWarningVisible, setIsMissingMediaWarningVisible] = useState(false)

  async function startCampaign() {
    postEvent(plausibleEvents.StartTextBlast, { id })

    setIsMissingMediaWarningVisible(false)

    updateCampaign(statusTypes.Approved)

    // setTimeout(() => {
    //   html2canvas(document.querySelector('#text-blast-estimation'))
    //     .then(canvas =>
    //       campaignApi.postCampaignEstimationScreenshot({
    //         contentId: id,
    //         screenshot: canvas.toDataURL('image/png', 1)
    //       })
    //     )
    //     .then(() => {
    //       updateCampaign(statusTypes.Approved)
    //     })
    // }, 100)
  }

  async function checkMediaAndStartCampaign() {
    if (moment(deliveryStartDate).isBefore(moment().startOf('day'))) {
      addToast('Start date should be in the future', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    if (moment(deliveryFinishDate).isBefore(moment().startOf('day'))) {
      addToast('End date should be in the future', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    if (moment(deliveryFinishDate).isBefore(moment(deliveryStartDate))) {
      addToast('End date should be after start date', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    if (!isHotelSetupCompleted) {
      addToast(
        'Your initial payment is pending. Please complete it to use drip campaigns.',
        {
          appearance: 'error',
          autoDismiss: true
        }
      )
      return
    }

    startCampaign()

    history.push('/custom-campaigns')

    // if (messageMedia) {
    //   startCampaign()
    // } else {
    //   setIsMissingMediaWarningVisible(true)
    // }
  }

  const { checkRateCodeUsage } = usePromoValidation(promotionCode)

  async function handleNext() {
    if (activeStep === steps.mainPromo && promotionCode) {
      const { messageType } = await checkRateCodeUsage(promotionCode)

      if (!messageType) {
        setActiveStep(nextStep)
      }

      setShowPromoUsageWarning(messageType)
    } else {
      setActiveStep(nextStep)
    }
  }

  if (showPromoUsageWarning) {
    return (
      <PromoUsageWarningDialog
        softMessage={true}
        promoCode={promotionCode}
        onClose={() => setShowPromoUsageWarning(false)}
        onConfirm={promotionCode}
        showPromoUsageWarning={showPromoUsageWarning}
      />
    )
  }

  if (isLoadingCampaign || isLoadingAudience || !messageTemplate) {
    return <LoadingIndicator />
  }

  if (isMissingMediaWarningVisible) {
    return (
      <ConfirmationDialog
        title="Do you want to start the campaign without a media?"
        onConfirm={() => startCampaign()}
        onClose={() => {
          setIsMissingMediaWarningVisible(false)
          setActiveStep(steps.mainTemplate)
        }}
        positiveMessage="Yes, start without media"
        negativeMessage="No, go back"
        hasDangerousAction
      >
        <p>
          Adding an image or video is <strong>strongly recommended</strong> to get the
          best results from drip campaigns.
        </p>
      </ConfirmationDialog>
    )
  }

  if (isConfirmationVisible) {
    return (
      <CampaignStartConfirmation
        startDate={deliveryStartDate}
        deliveryName={campaignData?.deliveryName}
        handleConfirm={() => startCampaign()}
        handleCancel={() => setIsConfirmationVisible(false)}
        audienceId={audienceId}
      />
    )
  }

  return (
    <Dialog>
      <div className={styles.resolutionWrapper} ref={wrapperRef}>
        <div
          className={
            isNestedDialogActive ? styles.dialogWrapperNested : styles.dialogWrapper
          }
        >
          <div className={styles.mainContent}>
            <div className={styles.navigationSidebar}>
              <div>
                <div className={styles.dialogDesc}>
                  <span className={styles.deliveryName}>
                    {campaignData?.deliveryName.replace('(', '\n(')}
                  </span>
                  <p
                    className={styles.status}
                    style={{ color: getCampaignStatusColor(customDeliveryStatus) }}
                  >
                    <span className={styles.dot}>
                      <Dot fill={getCampaignStatusColor(customDeliveryStatus)} />
                    </span>
                    {customDeliveryStatus}
                  </p>
                  <Link className={styles.closeButton} to="/custom-campaigns">
                    <CloseIcon />
                  </Link>
                </div>
              </div>
              <div className={styles.controls}>
                <div className={styles.steps}>
                  {getSteps(medium).map(step => (
                    <button
                      key={step}
                      onClick={() => setActiveStep(step)}
                      className={step === activeStep ? styles.activeStep : styles.step}
                    >
                      <span className={styles.paginatorNo}>{stepNames[step]}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className={styles.editTextHolder}>
              <div className={styles.editTextContent}>
                {activeStep === steps.campaignSetup && (
                  <CampaignSetup
                    audienceId={audienceId}
                    audiences={audiences}
                    isEdibable={canModifySetup}
                    deliveryStartDate={deliveryStartDate}
                    setDeliveryStartDate={setDeliveryStartDate}
                    setDeliveryFinishDate={setDeliveryFinishDate}
                    deliveryFinishDate={deliveryFinishDate}
                    setAudienceId={setAudienceId}
                    setMedium={setMedium}
                    medium={medium}
                    isAudienceCalculating={isAudienceCalculating}
                  />
                )}
                {activeStep === steps.mainTemplate && (
                  <MainTemplate
                    messageTemplate={messageTemplate}
                    onTextChange={handleTextChange}
                    usedAllTags={usedAllTags}
                    setUsedAllTags={setUsedAllTags}
                    setMessageMedia={setMessageMedia}
                    messageMedia={messageMedia}
                    isAllTagsRequired={true}
                    mediaTag={mediaTag}
                    requiredTagIds={requiredTagIds}
                    isMediaRequired={true}
                    customUrl={customUrl}
                    setCustomUrl={setCustomUrl}
                    isCustomUrlAllowed={true}
                  />
                )}
                {activeStep === steps.mainPromo && (
                  <MainPromo
                    promotionCode={promotionCode}
                    setPromotionCode={setPromotionCode}
                    minNightsToStay={minNightsToStay}
                    setMinNightsToStay={setMinNightsToStay}
                    maxDiscountPercentage={maxDiscountPercentage}
                    setMaxDiscountPercentage={setMaxDiscountPercentage}
                    setPromoTagline={setPromoTagline}
                    promoTagline={promoTagline}
                    hasPromoValidityDate={hasPromoValidityDate}
                    setHasPromoValidityDate={setHasPromoValidityDate}
                    promoValidityStartDate={promoValidityStartDate}
                    setPromoValidityStartDate={setPromoValidityStartDate}
                    promoValidityEndDate={promoValidityEndDate}
                    setPromoValidityEndDate={setPromoValidityEndDate}
                  />
                )}
                {activeStep === steps.email && (
                  <EmailEditorPanel
                    onSaveAndClose={updateCampaignEmail}
                    onClose={refreshEmailData}
                  />
                )}
              </div>
            </div>
          </div>
          <div className={styles.submitHolder}>
            {customDeliveryStatus === statusTypes.Draft && (
              <>
                <SecondaryRegularButton onClick={() => updateCampaign(statusTypes.Draft)}>
                  Save changes
                </SecondaryRegularButton>
                {nextStep ? (
                  <PrimaryRegularButton
                    className={styles.nextButton}
                    onClick={handleNext}
                    disabled={isNextDisabled}
                  >
                    Next
                  </PrimaryRegularButton>
                ) : (
                  <PrimaryRegularButton
                    disabled={!isValidated}
                    onClick={checkMediaAndStartCampaign}
                  >
                    Start campaign
                  </PrimaryRegularButton>
                )}
              </>
            )}
            {customDeliveryStatus === statusTypes.Approved && (
              <>
                <SecondaryRegularButton onClick={() => updateCampaign(statusTypes.Draft)}>
                  Convert to draft
                </SecondaryRegularButton>
                <PrimaryRegularButton
                  disabled={!isValidated}
                  onClick={() => updateCampaign()}
                >
                  Save campaign
                </PrimaryRegularButton>
              </>
            )}
            {customDeliveryStatus === statusTypes.Started && (
              <>
                <SecondaryRegularButton
                  onClick={() => updateCampaign(statusTypes.Interrupted)}
                >
                  Abort campaign
                </SecondaryRegularButton>
                <PrimaryRegularButton
                  disabled={!isValidated}
                  onClick={() => updateCampaign()}
                >
                  Save campaign
                </PrimaryRegularButton>
              </>
            )}
          </div>
        </div>
        <div className={styles.previewHolder}>
          {activeStep !== steps.email && dripMediumOptionsWithText.includes(medium) && (
            <TextPreview>
              <>
                <TextPreviewBubble
                  defaultData={{ userName, hotelName }}
                  hasLink
                  text={`${messageTemplate}`}
                />
                {messageMedia && isCampaignImageVisible(messageMedia) && (
                  <img
                    src={getImgUrlFromCDN(messageMedia, mediaTag, hotelId)}
                    alt="campaign media"
                    className={styles.campaignMedia}
                  />
                )}
                {messageMedia && getExtensionFromUrl(messageMedia) === 'pdf' && (
                  <div className={styles.campaignFile}>
                    <FileIcon extension="pdf" />
                    <span />
                  </div>
                )}
                <TextPreviewBubble defaultData={{ userName, hotelName }} />
              </>
            </TextPreview>
          )}
          {activeStep === steps.email && (
            <EmailPreview>
              <TestEmail />
            </EmailPreview>
          )}
          {medium === dripMediumOptions.Email && activeStep === steps.mainPromo && (
            <EmailPreview isEditable={false}>
              <TestEmail />
            </EmailPreview>
          )}
        </div>
      </div>
    </Dialog>
  )
}

const EditCampaignWithProvider = () => {
  return (
    <EmailEditorProvider>
      <EditCampaign />
    </EmailEditorProvider>
  )
}

export default EditCampaignWithProvider
